import { ChevronDown, Check } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";

export interface TwSelectOption {
  value: string;
  label: string;
}

interface Props {
  value: string;
  options: TwSelectOption[];
  onChange: (value: string) => void;
  ariaLabel: string;
  title?: string;
  /** Extra class on the root for width tuning. */
  className?: string;
}

/**
 * Custom dropdown used in the terminal toolbar. Unlike a native <select>, the
 * OPEN list is fully styleable (dark panel, custom option rows, accent on the
 * selected one). Keyboard + click-outside + focus management are handled here
 * so it stays accessible despite not being a native control.
 */
export function TwSelect({ value, options, onChange, ariaLabel, title, className }: Props) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  // Stable id prefix so the listbox can point aria-activedescendant at the
  // active option — without it a screen reader announces nothing as the user
  // arrows through a focused-on-the-listbox (roving-index) menu.
  const baseId = useId();
  const optionId = (index: number) => `${baseId}-opt-${index}`;
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const selectedIndex = options.findIndex((option) => option.value === value);
  const selected = selectedIndex >= 0 ? options[selectedIndex] : undefined;

  // Close on outside pointer-down.
  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [open]);

  // On open: seed the active row to the current selection and move focus to
  // the menu so arrow keys work. On close: return focus to the trigger.
  useEffect(() => {
    if (open) {
      setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
      menuRef.current?.focus();
    }
  }, [open, selectedIndex]);

  // Keep the active row scrolled into view as it moves.
  useEffect(() => {
    if (!open || activeIndex < 0) return;
    const node = menuRef.current?.children[activeIndex] as HTMLElement | undefined;
    node?.scrollIntoView({ block: "nearest" });
  }, [open, activeIndex]);

  function commit(index: number) {
    const option = options[index];
    if (option) onChange(option.value);
    setOpen(false);
    triggerRef.current?.focus();
  }

  function onTriggerKeyDown(event: React.KeyboardEvent) {
    if (event.key === "ArrowDown" || event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setOpen(true);
    }
  }

  function onMenuKeyDown(event: React.KeyboardEvent) {
    switch (event.key) {
      case "Escape":
        event.preventDefault();
        setOpen(false);
        triggerRef.current?.focus();
        break;
      case "ArrowDown":
        event.preventDefault();
        setActiveIndex((index) => Math.min(options.length - 1, index + 1));
        break;
      case "ArrowUp":
        event.preventDefault();
        setActiveIndex((index) => Math.max(0, index - 1));
        break;
      case "Home":
        event.preventDefault();
        setActiveIndex(0);
        break;
      case "End":
        event.preventDefault();
        setActiveIndex(options.length - 1);
        break;
      case "Enter":
      case " ":
        event.preventDefault();
        commit(activeIndex);
        break;
      case "Tab":
        setOpen(false);
        break;
    }
  }

  return (
    <div className={`tw-dd ${className || ""}`} ref={rootRef}>
      <button
        type="button"
        ref={triggerRef}
        className="tw-dd__trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        title={title}
        onClick={() => setOpen((value) => !value)}
        onKeyDown={onTriggerKeyDown}
      >
        <span className="tw-dd__value">{selected?.label ?? value}</span>
        <ChevronDown size={13} className="tw-dd__chevron" aria-hidden="true" />
      </button>
      {open ? (
        <div
          className="tw-dd__menu"
          role="listbox"
          aria-label={ariaLabel}
          aria-activedescendant={activeIndex >= 0 ? optionId(activeIndex) : undefined}
          tabIndex={-1}
          ref={menuRef}
          onKeyDown={onMenuKeyDown}
        >
          {options.map((option, index) => (
            <div
              key={option.value}
              id={optionId(index)}
              role="option"
              aria-selected={option.value === value}
              className={`tw-dd__opt${option.value === value ? " is-selected" : ""}${
                index === activeIndex ? " is-active" : ""
              }`}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => commit(index)}
            >
              <span className="tw-dd__opt-check" aria-hidden="true">
                {option.value === value ? <Check size={13} /> : null}
              </span>
              <span className="tw-dd__opt-label">{option.label}</span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
